import { useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { Button } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import PaymentCardDropDown from 'components/park/detail/PaymentCardDropDown';
import VehicleSelectDropDown from 'components/park/detail/VehicleSelectDropDown';
import ParkingSignInButton from 'components/park/ParkingSignInButton';
import {
  FooterButtonContainer,
  VerticalDistributedContainer,
} from 'components/sheets/SheetLayout';
import AccountContext from 'context/AccountContext';
import ParkingContext from 'context/ParkingContext';
import useAddPaymentMethod from 'hooks/useAddPaymentMethod';
import useCreateSession from 'hooks/useCreateSession';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useOngoingChargingSession from 'hooks/useOngoingChargingSession';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import { SelectedPaymentMethod } from 'hooks/useSelectedPaymentMethod';
import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { onboardingCompleted } from 'states/persistInStorage';
import { useAuthStore } from 'stores/authStore';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { Charger } from 'types/common';
import { Vehicle } from 'types/generatedSchemaTypes';
import { ParkNavProps, RootNavProps } from 'types/navigation';
import { createAction } from 'utils/analytics';
import {
  arePaymentMethodsEqual,
  isCorporateAccountPaymentOption,
} from 'utils/paymentUtils';
import { useUiStore } from 'stores/uiStore';

type DefineDisabledProps = {
  parkingZoneUid: string | undefined;
  selectedEvCharger: Charger | undefined;
};

const defineDisabled = ({
  parkingZoneUid,
  selectedEvCharger,
}: DefineDisabledProps) => {
  return !parkingZoneUid || !selectedEvCharger;
};

const StartChargingButton: React.FC = () => {
  const { navigate } = useNavigation<RootNavProps>();
  const { navigate: navigatePark } = useNavigation<ParkNavProps>();
  const { t } = useTranslation();
  const { selectedEvCharger } = useContext(ParkingContext) || {};

  const {
    parkingConditions,
    currentPaymentMethodId,
    selectedParkingVehiclePlate,
    setParkingConditions,
    setParkingVehiclePlate,
  } = useParkingStore((state) => state);

  const { selectedParkingZoneId, parkingZoneCountryCode, paymentMethod } =
    parkingConditions;
  const selectedVehicle = useSelectedParkingVehicle(
    false,
    undefined,
    selectedParkingVehiclePlate
  );
  const {
    customer: { applicationPreferences, vehicles },
  } = useContext(AccountContext);
  const { isLoggedIn, loginLoading } = useIsLoggedIn();
  const { ongoingChargingSession } = useOngoingChargingSession();
  const ongoingChargingChanged = useMemo(() => {
    return ongoingChargingSession;
  }, [ongoingChargingSession]);
  const prevSessions = useRef(ongoingChargingChanged);
  const chargingSessionChange = prevSessions.current !== ongoingChargingChanged;
  const isUserRestricted = useAuthStore((state) => state.accountRestricted);
  const hasVehicles = !!(vehicles && vehicles.length > 0);
  const { addCard, loadingAddCard } = useAddPaymentMethod();
  const selectedChargingSegment = useParkzoneStore(
    (state) => state.selectedChargingPricingInfoSegment
  );

  const { createCharging, createChargingLoading, chargingSuccess } =
    useCreateSession({});

  const hasValidPaymentMethod = useMemo(
    () =>
      !!(
        paymentMethod?.cardId ||
        paymentMethod?.benefitTargetUid ||
        isCorporateAccountPaymentOption(paymentMethod) ||
        paymentMethod?.method === 'MOBILEPAY'
      ),
    [paymentMethod]
  );

  const missingDetails = useMemo(
    () =>
      !hasValidPaymentMethod ||
      (!selectedVehicle && !applicationPreferences?.alwaysSelectVehicle),
    [
      hasValidPaymentMethod,
      selectedVehicle,
      applicationPreferences?.alwaysSelectVehicle,
    ]
  );

  const setVehicleState = useCallback(
    (vehicle?: Vehicle) => {
      // Only update if the vehicle actually changed
      if (vehicle?.licensePlate?.plateText !== selectedParkingVehiclePlate) {
        setParkingVehiclePlate(vehicle?.licensePlate?.plateText);
      }
    },
    [selectedParkingVehiclePlate, setParkingVehiclePlate]
  );

  const setCardState = useCallback(
    (selectedPaymentMethod?: SelectedPaymentMethod) => {
      if (!arePaymentMethodsEqual(selectedPaymentMethod, paymentMethod)) {
        setParkingConditions({ paymentMethod: selectedPaymentMethod });
      }
    },
    [setParkingConditions, paymentMethod]
  );

  const chargingSuccessAndChargingSessionNotChanged =
    chargingSuccess && !chargingSessionChange;
  const disabled =
    createChargingLoading ||
    defineDisabled({
      parkingZoneUid: selectedParkingZoneId,
      selectedEvCharger,
    }) ||
    chargingSuccessAndChargingSessionNotChanged;

  const buttonText = () => {
    if (!selectedEvCharger) {
      return t('evCharging.selectCharger');
    }
    if (!hasValidPaymentMethod) {
      return t('onboarding.addPaymentMethod');
    }
    if (!selectedVehicle && !hasVehicles) {
      return t('onboarding.addVehicle');
    }
    if (!selectedVehicle && hasVehicles) {
      return t('parking.parkingProcess.noVehicleSelected');
    }
    if (createChargingLoading || (chargingSuccess && !chargingSessionChange)) {
      return t('evCharging.startChargingLoading');
    }
    return t('button.start');
  };

  const handleStartCharging = async () => {
    if (isUserRestricted) {
      useUiStore.getState().setSheetModal('accountRestricted');
    } else if (
      !hasValidPaymentMethod &&
      !selectedVehicle &&
      !onboardingCompleted()
    ) {
      // Redirect to onboarding flow
      navigate('onboardingModal', {
        screen: 'welcome',
      });
    } else if (!hasValidPaymentMethod) {
      // Redirect to add payment method screen under account
      addCard();
    } else if (!selectedVehicle && !hasVehicles) {
      // Redirect to add vehicle screen
      navigate('addVehicleParking');
    } else if (!selectedVehicle && hasVehicles) {
      useUiStore.getState().setSheetModal('parkingVehicle');
    } else if (selectedEvCharger?.evse.status !== 'OCCUPIED') {
      navigatePark('chargeConnection', {
        parkingZoneUid: selectedParkingZoneId || '',
        chargingStationId: selectedEvCharger?.id || '',
      });
    } else {
      createAction('TAP', 'CREATE_CHARGING_SESSION', {
        selectedParkingZoneId,
      });
      if (!selectedChargingSegment) {
        showToast('error.genericError', 'error');
        return;
      }
      await createCharging();
    }
  };

  if (!isLoggedIn) {
    return (
      <ParkingSignInButton
        testID="park-zone-details-login"
        loading={loginLoading}
      />
    );
  }

  return (
    <FooterButtonContainer pb={0}>
      <VerticalDistributedContainer>
        <FooterButtonContainer pb={0}>
          <VehicleSelectDropDown
            setVehicleState={setVehicleState}
            currentVehiclePlate={selectedParkingVehiclePlate}
          />
          <PaymentCardDropDown
            parkingZoneCountryCode={parkingZoneCountryCode}
            currentPaymentMethodId={currentPaymentMethodId}
            setCardState={setCardState}
          />
        </FooterButtonContainer>
        <FooterButtonContainer pb={0}>
          <Button
            buttonType={missingDetails ? 'secondary' : 'primary'}
            size="l"
            loading={
              createChargingLoading ||
              (chargingSuccess && !chargingSessionChange) ||
              loadingAddCard
            }
            text={buttonText()}
            testID="park-zone-start-charging-button"
            disabled={disabled}
            onPress={handleStartCharging}
          />
        </FooterButtonContainer>
      </VerticalDistributedContainer>
    </FooterButtonContainer>
  );
};

export default StartChargingButton;
